import { useRef } from "react";

import { mergeStyleSets, Option, useTheme } from "@bps/fluent-ui";
import { ContactStatus } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { getNewPickerStyles } from "@ui-components/form/utils.ts";
import { ContactsSelect } from "@ui-components/selects/ContactsSelect.tsx";

import {
  convertSearchValueToBirthdayString,
  hasValidBirthdayData,
  hasValidNameData,
  hasValidPhoneNumberData
} from "./contact-picker.utils.ts";
import { ContactPickerProps } from "./ContactPicker.types.ts";
import { ContactPickerFieldContent } from "./ContactPickerFieldContent.tsx";
import { ContactPickerSuggestionItem } from "./ContactPickerSuggestionItemNew.tsx";

export const ContactPicker: React.FC<ContactPickerProps> = ({
  filter,
  styles,
  selectedKey,
  onChange,
  inputProps,
  includeDeceased,
  includeInactive,
  tagInUseKeys,
  onInputChange,
  autoFocus,
  ...selectProps
}) => {
  const { practice } = useStores();
  const theme = useTheme();

  const searchRef = useRef<"phoneNumber" | "name">();
  const searchValueRef = useRef<string>();

  const getContactFilter = (search: string) => {
    const statuses: ContactStatus[] = [
      ContactStatus.Active,
      ContactStatus.Inactive,
      ContactStatus.Deceased
    ]
      .filter(s => !(!includeInactive && s === ContactStatus.Inactive))
      .filter(s => !(!includeDeceased && s === ContactStatus.Deceased));

    const isValidBirthdayDate = hasValidBirthdayData(search);
    const isValidPhoneNumber = hasValidPhoneNumberData(search);
    const isValidName = hasValidNameData(search);
    const birthdayString = convertSearchValueToBirthdayString(search);
    searchRef.current = isValidPhoneNumber ? "phoneNumber" : undefined;
    searchValueRef.current = isValidPhoneNumber ? search : undefined;
    onInputChange?.(search);

    return {
      statuses,
      ...filter,
      search: isValidName && !isValidPhoneNumber ? search : undefined,
      birthday: isValidBirthdayDate ? birthdayString : undefined,
      phoneNumber: isValidPhoneNumber ? search : undefined
    };
  };

  const onChangeSelectedKeys = (key: string | undefined) => {
    return onChange?.(key, key ? practice.contactsMap.get(key) : undefined);
  };

  return (
    <ContactsSelect
      {...selectProps}
      {...inputProps}
      showOptionsCallout={autoFocus}
      noOptionsText="No matches found"
      selectedKeys={selectedKey}
      onChangeSelectedKeys={onChangeSelectedKeys}
      contactFilter={getContactFilter}
      onRenderFieldContent={(options: Option<Contact>[]) => {
        const contact = options[0]?.data;

        return contact ? (
          <ContactPickerFieldContent contact={contact} />
        ) : undefined;
      }}
      onRenderOption={option => (
        <ContactPickerSuggestionItem
          option={option}
          context={{
            searchBy: searchRef.current,
            searchValue: searchValueRef.current,
            inUse:
              tagInUseKeys && option.key
                ? tagInUseKeys.includes(option.key)
                : undefined
          }}
        />
      )}
      styles={mergeStyleSets(
        {
          fieldContent: getNewPickerStyles(
            theme,
            { hasErrorMessage: false, disabled: selectProps.disabled },
            {}
          ).text
        },
        {
          singleOption: {
            padding: "4px 12px"
          }
        },
        styles
      )}
    />
  );
};
